/**
 * Error Utilities
 * エラーハンドリングのための共通ユーティリティ
 */

import { toast } from 'sonner'

// ============================================
// エラータイプ定義
// ============================================

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical'

export interface AppError extends Error {
  code?: string
  severity?: ErrorSeverity
  context?: Record<string, unknown>
  userMessage?: string
}

// ============================================
// エラー作成ヘルパー
// ============================================

export function createAppError(
  message: string,
  options?: {
    code?: string
    severity?: ErrorSeverity
    context?: Record<string, unknown>
    userMessage?: string
    cause?: Error
  }
): AppError {
  const error = new Error(message) as AppError
  error.code = options?.code
  error.severity = options?.severity || 'error'
  error.context = options?.context
  error.userMessage = options?.userMessage || message
  if (options?.cause) {
    error.cause = options.cause
  }
  return error
}

// ============================================
// エラーメッセージ変換
// ============================================

const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  'Failed to fetch': 'ネットワークに接続できません。接続を確認してください。',
  // Safari / iOS・iPadOS はネットワーク失敗を "Load failed" で返す（Chrome の "Failed to fetch" 相当）。
  // これが無いと iPad の利用者だけが汎用の「予期しないエラー」表示になり、通信の問題だと分からない。
  'Load failed': 'ネットワークに接続できません。接続を確認してください。',
  NetworkError: 'ネットワークエラーが発生しました。',
  ECONNREFUSED: 'サーバーに接続できません。',

  // Auth errors
  'Invalid login credentials': 'メールアドレスまたはパスワードが正しくありません。',
  'Email not confirmed': 'メールアドレスの確認が完了していません。',
  'User not found': 'ユーザーが見つかりません。',

  // Supabase errors
  PGRST116: 'データが見つかりません。',
  PGRST301: 'アクセス権限がありません。',
  '23505': 'データが重複しています。',
  '23503': '関連するデータが存在しません。',

  // Validation errors
  Required: '必須項目が入力されていません。',
  'Invalid format': '入力形式が正しくありません。',

  // File errors
  'File too large': 'ファイルサイズが大きすぎます。',
  'Invalid file type': 'サポートされていないファイル形式です。',

  // Generic
  'Unknown error': '予期しないエラーが発生しました。',
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Check for known error messages
    for (const [key, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.includes(key)) {
        return message
      }
    }

    // Check for AppError with user message
    if ((error as AppError).userMessage) {
      return (error as AppError).userMessage!
    }

    // Return original message in development
    if (process.env.NODE_ENV === 'development') {
      return error.message
    }

    return ERROR_MESSAGES['Unknown error']!
  }

  if (typeof error === 'string') {
    return error
  }

  return ERROR_MESSAGES['Unknown error']!
}

// ============================================
// エラー通知
// ============================================

export function notifyError(error: unknown, options?: { title?: string }): void {
  const message = getErrorMessage(error)
  const title = options?.title || 'エラー'

  toast.error(title, {
    description: message,
    duration: 5000,
  })
}

export function notifySuccess(message: string, options?: { title?: string }): void {
  toast.success(options?.title || '成功', {
    description: message,
    duration: 3000,
  })
}

export function notifyWarning(message: string, options?: { title?: string }): void {
  toast.warning(options?.title || '注意', {
    description: message,
    duration: 4000,
  })
}

// ============================================
// 安全な非同期処理
// ============================================

export async function tryCatch<T>(
  fn: () => Promise<T>,
  options?: {
    errorMessage?: string
    showToast?: boolean
    onError?: (error: unknown) => void
  }
): Promise<[T, null] | [null, Error]> {
  try {
    const result = await fn()
    return [result, null]
  } catch (error) {
    if (options?.showToast !== false) {
      notifyError(error, { title: options?.errorMessage })
    }

    if (options?.onError) {
      options.onError(error)
    }

    return [null, error instanceof Error ? error : new Error(String(error))]
  }
}

// ============================================
// バリデーションエラー
// ============================================

export interface ValidationError {
  field: string
  message: string
}

export function createValidationError(errors: ValidationError[]): AppError {
  const message = errors.map((e) => `${e.field}: ${e.message}`).join(', ')
  return createAppError(message, {
    code: 'VALIDATION_ERROR',
    severity: 'warning',
    userMessage: errors[0]?.message || '入力内容を確認してください。',
    context: { errors },
  })
}

// ============================================
// API エラーハンドリング
// ============================================

export function handleApiError(response: Response): never {
  const error = createAppError(`API Error: ${response.status}`, {
    code: `HTTP_${response.status}`,
    severity: response.status >= 500 ? 'critical' : 'error',
    context: {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
    },
    userMessage: getHttpErrorMessage(response.status),
  })
  throw error
}

function getHttpErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'リクエストが不正です。'
    case 401:
      return 'ログインが必要です。'
    case 403:
      return 'アクセス権限がありません。'
    case 404:
      return 'データが見つかりません。'
    case 409:
      return 'データが競合しています。'
    case 422:
      return '入力内容を確認してください。'
    case 429:
      return 'リクエストが多すぎます。しばらくお待ちください。'
    case 500:
      return 'サーバーエラーが発生しました。'
    case 502:
    case 503:
    case 504:
      return 'サーバーが一時的に利用できません。'
    default:
      return '通信エラーが発生しました。'
  }
}

// ============================================
// リトライ機能
// ============================================

/**
 * 指数バックオフの待ち時間を「半分は固定・半分は乱数」で散らす（equal jitter）。
 *
 * なぜ要るか（2026-08-26 本番実測）:
 *   Supavisor の client 上限 200 に張り付いたとき、24 時間で 424 件の EMAXCONN が
 *   **30 秒に集中**した（routes: /api/construction-progress, /api/bt-progress/realtime）。
 *   ジッタが無いと、同時に失敗した全リクエストが 500ms・1000ms の**同じ瞬間**に
 *   再試行し、同期した波でプールを殴り直す（thundering herd）。
 *
 * 全部を乱数にする full jitter は 0ms 再試行を許してしまい、枯渇中は逆効果になり得る。
 * 半分を固定にして「最低でも base/2 は待つ」ことを保証しつつ、残り半分で波をばらす。
 *
 * @param base バックオフの基準値（ms）
 * @param random 0..1 を返す関数（テストから決定的に差し替えられるようにする）
 */
export function jitteredWait(base: number, random: () => number = Math.random): number {
  if (!Number.isFinite(base) || base <= 0) return 0
  const half = base / 2
  return Math.round(half + random() * half)
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: {
    maxRetries?: number
    delay?: number
    backoff?: boolean
    retryOn?: (error: unknown) => boolean
    /** テスト用。0..1 を返す。既定は Math.random */
    random?: () => number
  }
): Promise<T> {
  const maxRetries = options?.maxRetries ?? 3
  const delay = options?.delay ?? 1000
  const backoff = options?.backoff ?? true
  const random = options?.random ?? Math.random

  let lastError: unknown

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Check if we should retry
      if (options?.retryOn && !options.retryOn(error)) {
        throw error
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break
      }

      // Wait before retrying（ジッタで同期した再試行の波を崩す）
      const base = backoff ? delay * Math.pow(2, attempt) : delay
      const waitTime = jitteredWait(base, random)
      await new Promise((resolve) => setTimeout(resolve, waitTime))
    }
  }

  throw lastError
}
